import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BedIcon from "@mui/icons-material/Bed";
import FlightIcon from "@mui/icons-material/Flight";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import { excludeStaff } from "../../utils/timetableUtils";

function isTrue(value) {
  return value === true || value === 1;
}

function buildLabel(person) {
  return person?.personName || "Unknown";
}

function isResidential(person) {
  return person?.residential !== false;
}

function isAdult(person) {
  return person?.personType === "ADULT";
}

// Non-residential people are split by type: a non-residential STUDENT is a
// "day student", while a non-residential ADULT is "day staff". Both are valid
// (they don't stay overnight) but they are grouped and labelled separately.
function isDayStudent(person) {
  return !isResidential(person) && !isAdult(person);
}

function isDayStaff(person) {
  return !isResidential(person) && isAdult(person);
}

function dedupeByPerson(people = []) {
  const seen = new Set();
  return people.filter((p) => {
    const key = `${p.personType}:${p.personId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function personIsReady(person) {
  if (isResidential(person)) return isTrue(person?.ready);
  // Non-residential people don't need a bed or transport — don't flag them
  return true;
}

function getStatusCardStyles(people = [], fallback = "yellow") {
  if (!people.length) {
    return {
      backgroundColor: "#f5f5f5",
      border: "1px solid #dddddd",
    };
  }

  const allReady = people.every(personIsReady);

  if (allReady) {
    return {
      backgroundColor: "#eaf4ea",
      border: "1px solid #b9d7b9",
    };
  }

  if (fallback === "red") {
    return {
      backgroundColor: "#fdeaea",
      border: "1px solid #e6b3b3",
    };
  }

  if (fallback === "blue") {
    return {
      backgroundColor: "#eaf2fd",
      border: "1px solid #b8cdee",
    };
  }

  return {
    backgroundColor: "#fff8e6",
    border: "1px solid #ead79c",
  };
}

function TravelPersonRow({ person }) {
  const residential = isResidential(person);
  const ready = personIsReady(person);

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      key={`${person.personType}:${person.personId}`}
      sx={{ minHeight: 32 }}
    >
      {isTrue(person?.hasTransfer) && (
        <FlightIcon fontSize="small" />
      )}

      {(isTrue(person?.hasTransport) || isTrue(person?.privatePickup)) && (
        <DirectionsBusIcon fontSize="small" />
      )}

      {residential && isTrue(person?.hasRoomForTonight) && (
        <BedIcon fontSize="small" />
      )}

      <Chip
        label={
          <span
            style={{
              fontWeight: person?.personType === "ADULT" ? 700 : 400,
            }}
          >
            {buildLabel(person)}
          </span>
        }
        color={ready ? "default" : "warning"}
        size="small"
      />

      {!residential && (
        <Chip label="Day" size="small" variant="outlined" sx={{ fontSize: "0.65rem", height: 20 }} />
      )}

      {isTrue(person?.um) && (
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: "warning.main" }}
        >
          UM
        </Typography>
      )}
    </Stack>
  );
}

function TravelGroupCard({ title, people, cardStyles, tv = false, tvColumns = 3 }) {
  const [openState, setOpen] = useState(false);
  const open = tv || openState;

  if (!people.length) return null;

  const readyCount = people.filter(personIsReady).length;
  const residentialCount = people.filter(isResidential).length;
  const dayCount = people.length - residentialCount;
  // "ready" is a residential concept (bed/transport); "day" is only worth
  // calling out when a card mixes residential and day people. In an all-day
  // card (Day Students / Day Staff) both are noise, so we hide them.
  const showReady = residentialCount > 0;
  const showDay = residentialCount > 0 && dayCount > 0;

  return (
    <Card
      sx={{
        width: tv ? tvColumns * 250 : 320,
        ...cardStyles,
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {people.length} {people.length === 1 ? "person" : "people"}
              {showReady && ` • ${readyCount} ready`}
              {showDay && ` • ${dayCount} day`}
            </Typography>
          </Box>

          {tv ? null : (
            <IconButton onClick={() => setOpen((prev) => !prev)}>
              <ExpandMoreIcon />
            </IconButton>
          )}
        </Stack>

        <Collapse in={open}>
          <Box sx={{ mt: 2, columnCount: tv ? tvColumns : 1, columnGap: "24px" }}>
            {people.map((person) => (
              <Box key={`${person.personType}:${person.personId}`} sx={{ breakInside: "avoid", mb: 1 }}>
                <TravelPersonRow person={person} />
              </Box>
            ))}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default function ArrivalsLeaversLane({
  travel = { arrivals: [], departures: [], internalTransfers: [] },
  selectedDate,
  selectedCampusId,
  onOpenBedrooms,
  onOpenTransfers,
  onOpenTransport,
  tv = false,
  tvColumns = 3,
  tvCardRange = null,
}) {
  const sortByName = (arr) =>
    [...arr].sort((a, b) =>
      String(a.personName || "").localeCompare(String(b.personName || ""))
    );

  // TV view drops our own staff from the travel lists (group adults stay).
  const t = useMemo(() => {
    if (!tv) return travel || {};
    return {
      arrivals: excludeStaff(travel?.arrivals || []),
      departures: excludeStaff(travel?.departures || []),
      internalTransfers: excludeStaff(travel?.internalTransfers || []),
    };
  }, [travel, tv]);

  const allArrivals = useMemo(() => sortByName(t?.arrivals || []), [t]);
  const arrivals = useMemo(() => allArrivals.filter(isResidential), [allArrivals]);

  // Non-residential people come from both arrivals and departures and may
  // appear in both, so dedupe before splitting students from staff.
  const dayStudents = useMemo(() => {
    const all = [...(t?.arrivals || []), ...(t?.departures || [])].filter(isDayStudent);
    return sortByName(dedupeByPerson(all));
  }, [t]);

  const dayStaff = useMemo(() => {
    const all = [...(t?.arrivals || []), ...(t?.departures || [])].filter(isDayStaff);
    return sortByName(dedupeByPerson(all));
  }, [t]);

  const allLeavers = useMemo(() => sortByName(t?.departures || []), [t]);
  const leavers = useMemo(() => allLeavers.filter(isResidential), [allLeavers]);

  const internalTransfers = useMemo(() => sortByName(t?.internalTransfers || []), [t]);

  const hasAnything = allArrivals.length || allLeavers.length || internalTransfers.length;
  if (!hasAnything) return null;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h6">Arrivals & Departures</Typography>

        {tv ? null : (
          <Stack direction="row" spacing={2}>
            <Typography
              variant="body2"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => onOpenTransfers?.(selectedDate)}
            >
              Open Transfers →
            </Typography>

            <Typography
              variant="body2"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => onOpenBedrooms?.(selectedDate, selectedCampusId)}
            >
              Open Bedrooms →
            </Typography>

            <Typography
              variant="body2"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => onOpenTransport?.(selectedDate, selectedCampusId)}
            >
              Open Transport →
            </Typography>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {(() => {
          // One ordered list of the non-empty cards so TV view can render a slice
          // when this lane spans several screens. Order/keys are stable.
          const cards = [
            { title: "Arriving Today", people: arrivals, fallback: "red" },
            { title: "Leaving Today", people: leavers, fallback: "yellow" },
            { title: "Internal Transfers", people: internalTransfers, fallback: "blue" },
            { title: "Day Students", people: dayStudents, fallback: "blue" },
            { title: "Day Staff", people: dayStaff, fallback: "blue" },
          ].filter((c) => c.people.length > 0);
          const shown = tvCardRange ? cards.slice(tvCardRange[0], tvCardRange[1]) : cards;
          return shown.map((c) => (
            <TravelGroupCard
              key={c.title}
              title={c.title}
              people={c.people}
              cardStyles={getStatusCardStyles(c.people, c.fallback)}
              tv={tv}
              tvColumns={tvColumns}
            />
          ));
        })()}
      </Stack>
    </Box>
  );
}